import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Square, Copy, Download, Eye, RotateCcw } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface BorderRadiusConfig {
  topLeft: number;
  topRight: number;
  bottomRight: number;
  bottomLeft: number;
  unit: 'px' | '%' | 'rem';
}

const CSSBorderRadiusGenerator: React.FC = () => {
  const [borderRadius, setBorderRadius] = useState<BorderRadiusConfig>({
    topLeft: 8,
    topRight: 8,
    bottomRight: 8,
    bottomLeft: 8,
    unit: 'px'
  });
  const [isUniform, setIsUniform] = useState(true);
  const [cssCode, setCssCode] = useState('');

  useSEO({
    title: 'CSS Border Radius Generator - Create Rounded Corners Online',
    description: 'Generate CSS border-radius code with visual preview. Create rounded corners for elements with individual corner control. Copy ready-to-use CSS code.',
    keywords: 'css border radius generator, rounded corners css, border radius tool, css effects, web design tools',
    canonical: 'https://snap-tools.online/design/css-border-radius-generator'
  });

  // Generate CSS code
  useEffect(() => {
    const { topLeft, topRight, bottomRight, bottomLeft, unit } = borderRadius;
    
    let css: string;
    if (isUniform && topLeft === topRight && topRight === bottomRight && bottomRight === bottomLeft) {
      css = `border-radius: ${topLeft}${unit};`;
    } else {
      css = `border-radius: ${topLeft}${unit} ${topRight}${unit} ${bottomRight}${unit} ${bottomLeft}${unit};`;
    }
    
    setCssCode(css);
  }, [borderRadius, isUniform]);

  const updateRadius = (corner: keyof Omit<BorderRadiusConfig, 'unit'>, value: number) => {
    if (isUniform) {
      setBorderRadius(prev => ({
        ...prev,
        topLeft: value,
        topRight: value,
        bottomRight: value,
        bottomLeft: value
      }));
    } else {
      setBorderRadius(prev => ({
        ...prev,
        [corner]: value
      }));
    }
  };

  const updateUnit = (unit: 'px' | '%' | 'rem') => {
    setBorderRadius(prev => ({ ...prev, unit }));
  };

  const resetRadius = () => {
    setBorderRadius({
      topLeft: 8,
      topRight: 8,
      bottomRight: 8,
      bottomLeft: 8,
      unit: 'px'
    });
    setIsUniform(true);
  };

  const copyCss = () => {
    navigator.clipboard.writeText(cssCode);
    toast.success('CSS code copied to clipboard!');
  };

  const downloadCss = () => {
    const blob = new Blob([cssCode], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'border-radius.css';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSS file downloaded!');
  };

  const presets = [
    { name: 'None', values: { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 } },
    { name: 'Small', values: { topLeft: 4, topRight: 4, bottomRight: 4, bottomLeft: 4 } },
    { name: 'Medium', values: { topLeft: 8, topRight: 8, bottomRight: 8, bottomLeft: 8 } },
    { name: 'Large', values: { topLeft: 16, topRight: 16, bottomRight: 16, bottomLeft: 16 } },
    { name: 'Circle', values: { topLeft: 50, topRight: 50, bottomRight: 50, bottomLeft: 50 }, unit: '%' as const },
    { name: 'Pill', values: { topLeft: 25, topRight: 25, bottomRight: 25, bottomLeft: 25 } },
    { name: 'Leaf', values: { topLeft: 0, topRight: 50, bottomRight: 0, bottomLeft: 50 } },
    { name: 'Teardrop', values: { topLeft: 50, topRight: 0, bottomRight: 50, bottomLeft: 50 } }
  ];

  const applyPreset = (preset: typeof presets[0]) => {
    setBorderRadius(prev => ({
      ...prev,
      ...preset.values,
      unit: preset.unit || prev.unit
    }));
    setIsUniform(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-100 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl">
                <Square className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              CSS Border Radius Generator
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Create perfect rounded corners with visual preview. Control individual corners or use presets. Generate ready-to-use CSS code.
            </p>
          </div>

          {/* Header Ad */}
          <div className="mb-12">
            <AdContainer
              adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
              className="max-w-4xl mx-auto"
              position="header"
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Controls */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Border Radius Controls</h2>
              
              {/* Uniform Toggle */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isUniform}
                    onChange={(e) => setIsUniform(e.target.checked)}
                    className="mr-3"
                  />
                  <span className="font-medium text-gray-700">Uniform radius (all corners same)</span>
                </label>
              </div>

              {/* Unit Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                <div className="flex gap-2">
                  {(['px', '%', 'rem'] as const).map(unit => (
                    <button
                      key={unit}
                      onClick={() => updateUnit(unit)}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        borderRadius.unit === unit
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              </div>

              {/* Radius Controls */}
              <div className="space-y-4 mb-6">
                {isUniform ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Radius: {borderRadius.topLeft}{borderRadius.unit}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max={borderRadius.unit === '%' ? 50 : 100}
                      value={borderRadius.topLeft}
                      onChange={(e) => updateRadius('topLeft', Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Top Left: {borderRadius.topLeft}{borderRadius.unit}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max={borderRadius.unit === '%' ? 50 : 100}
                        value={borderRadius.topLeft}
                        onChange={(e) => updateRadius('topLeft', Number(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Top Right: {borderRadius.topRight}{borderRadius.unit}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max={borderRadius.unit === '%' ? 50 : 100}
                        value={borderRadius.topRight}
                        onChange={(e) => updateRadius('topRight', Number(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bottom Left: {borderRadius.bottomLeft}{borderRadius.unit}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max={borderRadius.unit === '%' ? 50 : 100}
                        value={borderRadius.bottomLeft}
                        onChange={(e) => updateRadius('bottomLeft', Number(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bottom Right: {borderRadius.bottomRight}{borderRadius.unit}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max={borderRadius.unit === '%' ? 50 : 100}
                        value={borderRadius.bottomRight}
                        onChange={(e) => updateRadius('bottomRight', Number(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Presets */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Presets</label>
                <div className="grid grid-cols-2 gap-2">
                  {presets.map(preset => (
                    <button
                      key={preset.name}
                      onClick={() => applyPreset(preset)}
                      className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={resetRadius}
                  className="btn-secondary flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              </div>
            </div>

            {/* Preview & Output */}
            <div className="space-y-8">
              {/* Preview */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Eye className="w-6 h-6" />
                  Preview
                </h2>
                <div className="bg-gray-100 p-8 rounded-lg flex items-center justify-center min-h-[300px]">
                  <div
                    className="w-48 h-32 bg-gradient-to-r from-blue-400 to-cyan-500"
                    style={{
                      borderTopLeftRadius: `${borderRadius.topLeft}${borderRadius.unit}`,
                      borderTopRightRadius: `${borderRadius.topRight}${borderRadius.unit}`,
                      borderBottomRightRadius: `${borderRadius.bottomRight}${borderRadius.unit}`,
                      borderBottomLeftRadius: `${borderRadius.bottomLeft}${borderRadius.unit}`
                    }}
                  />
                </div>
              </div>

              {/* CSS Output */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">CSS Code</h2>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                    <code>{cssCode}</code>
                  </pre>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={copyCss}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Copy CSS
                    </button>
                    <button
                      onClick={downloadCss}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Ad */}
          <div className="mt-12">
            <AdContainer
              adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
              className="max-w-4xl mx-auto"
              position="footer"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CSSBorderRadiusGenerator;
